// Audio system configuration
// Using Web Audio API to generate procedural sounds

export interface SoundConfig {
  id: string
  type: 'sfx' | 'music'
  volume: number
}

export const SOUND_IDS = {
  // UI sounds
  CLICK: 'click',
  SUCCESS: 'success',
  ERROR: 'error',
  NOTIFICATION: 'notification',
  ACHIEVEMENT: 'achievement',
  
  // Game sounds
  BUILD: 'build',
  COINS: 'coins',
  DAY_CHANGE: 'day_change',
  CUSTOMER_ARRIVE: 'customer_arrive',
  BALL_HIT: 'ball_hit',
  
  // Ambience
  CROWD: 'crowd',
  BIRDS: 'birds',
  
  // Music
  MENU_MUSIC: 'menu_music',
  GAME_MUSIC: 'game_music',
} as const

export type SoundId = typeof SOUND_IDS[keyof typeof SOUND_IDS]

// Audio context and nodes
let audioContext: AudioContext | null = null
let masterGain: GainNode | null = null
let musicGain: GainNode | null = null
let sfxGain: GainNode | null = null

export function initAudio() {
  if (audioContext) return
  
  audioContext = new AudioContext()
  
  masterGain = audioContext.createGain()
  masterGain.gain.value = 0.5
  masterGain.connect(audioContext.destination)
  
  musicGain = audioContext.createGain()
  musicGain.gain.value = 0.3
  musicGain.connect(masterGain)
  
  sfxGain = audioContext.createGain()
  sfxGain.gain.value = 0.6
  sfxGain.connect(masterGain)
}

export function setMasterVolume(volume: number) {
  if (masterGain) masterGain.gain.value = Math.max(0, Math.min(1, volume))
}

export function setMusicVolume(volume: number) {
  if (musicGain) musicGain.gain.value = Math.max(0, Math.min(1, volume))
}

export function setSfxVolume(volume: number) {
  if (sfxGain) sfxGain.gain.value = Math.max(0, Math.min(1, volume))
}

/** Shared music output channel for the music player (routed through musicGain
 *  so the music volume slider controls it). Initialises audio on demand. */
export function getMusicChannel(): { ctx: AudioContext; gain: GainNode } | null {
  if (!audioContext || !musicGain) {
    initAudio()
    if (!audioContext || !musicGain) return null
  }
  return { ctx: audioContext, gain: musicGain }
}

// Global SFX kill-switch. The current procedural beeps sound dated, so every
// sound effect is muted for now — a modern SFX pass will replace them later.
// Flip this to true (and swap the synth functions) to bring sound effects back.
// Music is unaffected (it lives in musicPlayer.ts, not here).
const SFX_ENABLED = false

// Generate procedural sounds using Web Audio API
export function playSound(soundId: SoundId) {
  if (!SFX_ENABLED) return

  if (!audioContext || !sfxGain) {
    initAudio()
    if (!audioContext || !sfxGain) return
  }
  
  // Resume context if suspended
  if (audioContext.state === 'suspended') {
    audioContext.resume()
  }
  
  switch (soundId) {
    case SOUND_IDS.CLICK:
      playClick()
      break
    case SOUND_IDS.SUCCESS:
      playSuccess()
      break
    case SOUND_IDS.ERROR:
      playError()
      break
    case SOUND_IDS.NOTIFICATION:
      playNotification()
      break
    case SOUND_IDS.ACHIEVEMENT:
      playAchievement()
      break
    case SOUND_IDS.BUILD:
      playBuild()
      break
    case SOUND_IDS.COINS:
      playCoins()
      break
    case SOUND_IDS.DAY_CHANGE:
      playDayChange()
      break
    case SOUND_IDS.BALL_HIT:
      playBallHit()
      break
  }
}

function playClick() {
  if (!audioContext || !sfxGain) return
  
  const osc = audioContext.createOscillator()
  const gain = audioContext.createGain()
  
  osc.type = 'sine'
  osc.frequency.setValueAtTime(800, audioContext.currentTime)
  osc.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.05)
  
  gain.gain.setValueAtTime(0.3, audioContext.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05)
  
  osc.connect(gain)
  gain.connect(sfxGain)
  
  osc.start()
  osc.stop(audioContext.currentTime + 0.05)
}

function playSuccess() {
  if (!audioContext || !sfxGain) return
  
  const notes = [523.25, 659.25, 783.99] // C5, E5, G5
  
  notes.forEach((freq, i) => {
    const osc = audioContext!.createOscillator()
    const gain = audioContext!.createGain()
    
    osc.type = 'sine'
    osc.frequency.value = freq
    
    const startTime = audioContext!.currentTime + i * 0.1
    gain.gain.setValueAtTime(0, startTime)
    gain.gain.linearRampToValueAtTime(0.2, startTime + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2)
    
    osc.connect(gain)
    gain.connect(sfxGain!)
    
    osc.start(startTime)
    osc.stop(startTime + 0.2)
  })
}

function playError() {
  if (!audioContext || !sfxGain) return
  
  const osc = audioContext.createOscillator()
  const gain = audioContext.createGain()
  
  osc.type = 'sawtooth'
  osc.frequency.setValueAtTime(200, audioContext.currentTime)
  osc.frequency.linearRampToValueAtTime(100, audioContext.currentTime + 0.2)
  
  gain.gain.setValueAtTime(0.2, audioContext.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2)
  
  osc.connect(gain)
  gain.connect(sfxGain)
  
  osc.start()
  osc.stop(audioContext.currentTime + 0.2)
}

function playNotification() {
  if (!audioContext || !sfxGain) return
  
  const osc = audioContext.createOscillator()
  const gain = audioContext.createGain()
  
  osc.type = 'sine'
  osc.frequency.setValueAtTime(880, audioContext.currentTime)
  osc.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1)
  
  gain.gain.setValueAtTime(0.2, audioContext.currentTime)
  gain.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.1)
  gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
  
  osc.connect(gain)
  gain.connect(sfxGain)
  
  osc.start()
  osc.stop(audioContext.currentTime + 0.3)
}

function playAchievement() {
  if (!audioContext || !sfxGain) return
  
  // Fanfare-like arpeggio: C5, E5, G5, C6
  const notes = [523.25, 659.25, 783.99, 1046.50]
  
  notes.forEach((freq, i) => {
    const osc = audioContext!.createOscillator()
    const gain = audioContext!.createGain()
    
    osc.type = 'triangle'
    osc.frequency.value = freq
    
    const startTime = audioContext!.currentTime + i * 0.12
    gain.gain.setValueAtTime(0, startTime)
    gain.gain.linearRampToValueAtTime(0.25, startTime + 0.03)
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.4)
    
    osc.connect(gain)
    gain.connect(sfxGain!)
    
    osc.start(startTime)
    osc.stop(startTime + 0.4)
  })
}

function playBuild() {
  if (!audioContext || !sfxGain) return
  
  // Construction sound: low thump + high ping
  const thump = audioContext.createOscillator()
  const ping = audioContext.createOscillator()
  const thumpGain = audioContext.createGain()
  const pingGain = audioContext.createGain()
  
  thump.type = 'sine'
  thump.frequency.setValueAtTime(100, audioContext.currentTime)
  thump.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime + 0.15)
  
  thumpGain.gain.setValueAtTime(0.4, audioContext.currentTime)
  thumpGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15)
  
  ping.type = 'sine'
  ping.frequency.value = 1200
  
  pingGain.gain.setValueAtTime(0, audioContext.currentTime + 0.05)
  pingGain.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.07)
  pingGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2)
  
  thump.connect(thumpGain)
  thumpGain.connect(sfxGain)
  ping.connect(pingGain)
  pingGain.connect(sfxGain)
  
  thump.start()
  thump.stop(audioContext.currentTime + 0.15)
  ping.start(audioContext.currentTime + 0.05)
  ping.stop(audioContext.currentTime + 0.2)
}

function playCoins() {
  if (!audioContext || !sfxGain) return
  
  // Coin collect sound: multiple high pings
  for (let i = 0; i < 3; i++) {
    const osc = audioContext.createOscillator()
    const gain = audioContext.createGain()
    
    osc.type = 'sine'
    osc.frequency.value = 1500 + i * 200
    
    const startTime = audioContext.currentTime + i * 0.04
    gain.gain.setValueAtTime(0.15, startTime)
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.1)
    
    osc.connect(gain)
    gain.connect(sfxGain)
    
    osc.start(startTime)
    osc.stop(startTime + 0.1)
  }
}

function playDayChange() {
  if (!audioContext || !sfxGain) return
  
  // Gentle chime for day change
  const osc = audioContext.createOscillator()
  const gain = audioContext.createGain()
  
  osc.type = 'sine'
  osc.frequency.setValueAtTime(600, audioContext.currentTime)
  osc.frequency.setValueAtTime(800, audioContext.currentTime + 0.15)
  
  gain.gain.setValueAtTime(0.1, audioContext.currentTime)
  gain.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.1)
  gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)
  
  osc.connect(gain)
  gain.connect(sfxGain)
  
  osc.start()
  osc.stop(audioContext.currentTime + 0.5)
}

function playBallHit() {
  if (!audioContext || !sfxGain) return
  
  // Padel ball hit: sharp attack, quick decay
  const osc = audioContext.createOscillator()
  const noise = audioContext.createOscillator()
  const gain = audioContext.createGain()
  const noiseGain = audioContext.createGain()
  
  osc.type = 'sine'
  osc.frequency.setValueAtTime(400, audioContext.currentTime)
  osc.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.08)
  
  gain.gain.setValueAtTime(0.25, audioContext.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.08)
  
  // Add some "crack" with square wave
  noise.type = 'square'
  noise.frequency.value = 150
  noiseGain.gain.setValueAtTime(0.08, audioContext.currentTime)
  noiseGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.03)
  
  osc.connect(gain)
  gain.connect(sfxGain)
  noise.connect(noiseGain)
  noiseGain.connect(sfxGain)
  
  osc.start()
  osc.stop(audioContext.currentTime + 0.08)
  noise.start()
  noise.stop(audioContext.currentTime + 0.03)
}

// NOTE: background music now lives in src/systems/musicPlayer.ts (a real
// track library + a richer generative engine). audioSystem only owns SFX and
// the shared gain channels.

// Audio settings store
export interface AudioSettings {
  masterVolume: number
  musicVolume: number
  sfxVolume: number
  musicEnabled: boolean
  sfxEnabled: boolean
}

export const defaultAudioSettings: AudioSettings = {
  masterVolume: 0.5,
  // Real (Pixabay) tracks are mastered louder than the generative grooves, so
  // the music bed sits quieter by default; players can raise it in Ajustes.
  musicVolume: 0.2,
  sfxVolume: 0.6,
  musicEnabled: true,
  sfxEnabled: true,
}
